/**
 * Maps technical/database error text from the API to a message a user can act on.
 *
 * The backend has no i18n layer of its own — it returns English strings — so this is where a
 * server-side error becomes a translated one. Matching stays on the ENGLISH technical marker
 * (a constraint name, a Hibernate phrase) because that is what actually arrives on the wire;
 * only the message shown to the user is localised.
 *
 * When no marker matches, the raw backend message is passed through untranslated rather than
 * replaced with a vague translated one. An untranslated but specific error tells a clinician
 * more than a translated but generic one, and it keeps the message reproducible in a bug report.
 */
import i18n from '../i18n';

/** Technical marker (matched as a substring of the API message) -> key in the `errors` namespace. */
const ERROR_KEY_MAP: Record<string, string> = {
  // Database constraints
  uk_clinic_ward_name: 'api.wardNameExists',
  uk_ward_bed: 'api.bedNumberExists',
  uk_patient_mobile: 'api.patientMobileExists',
  uk_user_email: 'api.userEmailExists',

  // Specific technical errors
  "column 'admission_number' cannot be null": 'api.admissionNumberFailed',
  'Bed is not available': 'api.bedUnavailable',
  'Admission not found': 'api.admissionNotFound',
  'Patient not found': 'api.patientNotFound',

  // ClinicalDataAccessInterceptor refusals (doctors-only clinical records): its fixed sentences, by area.
  'Patient clinical records are available to doctors only.': 'api.clinicalAccessDoctors',
  'Lab results are available to doctors and the lab team only.': 'api.clinicalAccessLab',
  'Prescriptions are available to doctors and the pharmacy only.': 'api.clinicalAccessPharmacy',
  'This inpatient record is available to doctors and nursing staff only.': 'api.clinicalAccessInpatient',
  'This part of the patient record is available to doctors and nursing staff only.': 'api.clinicalAccessNursing',
  'Eye exams are available to doctors, optometrists and nurses only.': 'api.clinicalAccessEyeExam',
  'Eye care records are available to doctors only.': 'api.clinicalAccessEye',
  'Dental records are available to doctors only.': 'api.clinicalAccessDental',

  // Generic categories
  'Access is denied': 'api.accessDenied',
  Unauthorized: 'api.sessionExpired',
  'Internal Server Error': 'api.internalServerError',
  'Bad Request': 'api.badRequest',
  Conflict: 'api.conflict',

  // Hibernate/Spring/SQL markers
  'Duplicate entry': 'api.duplicateEntry',
  'foreign key constraint': 'api.foreignKeyConstraint',
  'Data truncation': 'api.dataTruncation',
  'JSON parse error': 'api.jsonParseError',
  'could not execute statement': 'api.statementFailed',
  'Constraint violation': 'api.constraintViolation',
  'A technical error occurred': 'api.technicalError',
  'A database error occurred': 'api.databaseError',
  'no session': 'api.noSession',
};

/**
 * Coded refusals (the body's `errorCode` / `code`) -> key in the `errors` namespace. Checked before the message
 * markers, so the person reads the reason in their own language whatever English the server sent.
 */
const ERROR_CODE_KEY_MAP: Record<string, string> = {
  // Doctor specialty (medopd-docs 155): dental chart writes are for dentists, eye exam writes for eye doctors.
  DENTISTS_ONLY: 'api.dentistsOnly',
  EYE_DOCTORS_ONLY: 'api.eyeDoctorsOnly',
  NOT_AN_EYE_VISIT: 'api.notAnEyeVisit',
};

function translate(key: string): string {
  return i18n.t(key, { ns: 'errors' });
}

/**
 * Maps a technical error message to a friendly, localised one.
 * Returns the original message when nothing matches and it does not look like a stack trace.
 */
export function getFriendlyErrorMessage(message: string, code?: string | null): string {
  if (code && ERROR_CODE_KEY_MAP[code]) return translate(ERROR_CODE_KEY_MAP[code]);
  if (!message) return translate('generic.unexpected');

  // @RequiresModule (PermissionAspect): a feature the super admin hasn't switched on for this clinic.
  if (/^Access denied: module '[A-Z_]+' is not enabled for this clinic\.?$/.test(message.trim())) {
    return translate('api.featureOff');
  }

  for (const [technical, key] of Object.entries(ERROR_KEY_MAP)) {
    if (message.includes(technical)) {
      return translate(key);
    }
  }

  // Java/Hibernate internals must never reach a user, in any language.
  if (
    message.includes('java.') ||
    message.includes('org.hibernate') ||
    message.includes('SqlException')
  ) {
    return translate('api.databaseContactSupport');
  }

  if (message.toLowerCase() === 'an error occurred') {
    return translate('generic.tryAgain');
  }

  return message;
}

/**
 * The standard way to turn a caught API error into a toast message.
 *
 * Prefer this over reading `err.response.data.message` inline: it runs the backend's English
 * text through the mapping above, and falls back to a translated, screen-specific message when
 * the server said nothing useful.
 *
 *   toast.error(apiErrorMessage(err, t('errors:generic.tryAgain')));
 */
export function apiErrorMessage(error: unknown, fallback?: string): string {
  const raw =
    (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data
      ?.message ??
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
    (error as { message?: string })?.message;

  if (typeof raw === 'string' && raw.trim()) {
    const friendly = getFriendlyErrorMessage(raw);
    // A network failure surfaces as a bare "Network Error"/"Failed to fetch", which tells the
    // user nothing about what to do — replace it with the actionable translated message.
    if (/network error|failed to fetch|load failed/i.test(friendly)) {
      return translate('generic.network');
    }
    return friendly;
  }

  return fallback ?? translate('generic.unexpected');
}
